import { createApi } from "@reduxjs/toolkit/query/react";
import { DynamicBaseQuery } from "./baseQuery";
import { FloorsIndexResponse } from "./types/locations/responses/FloorsIndexResponse";
import { FloorModel } from "./types/locations/models/FloorModel";
import { FloorRequest } from "./types/locations/requests/FloorRequest";
import { SuccessResponse } from "./types/common/responses/SuccessResponse";
import { RoomsIndexResponse } from "./types/locations/responses/RoomsIndexResponse";
import { RoomRequest } from "./types/locations/requests/RoomRequest";
import { BedRequest } from "./types/locations/requests/BedRequest";
import { LocationModel } from "./types/authorisation/models/LocationModel";
import { LocationsIndexResponse } from "./types/locations/responses/LocationsIndexResponse";
import { RoomIndexRequest } from "./types/locations/requests/RoomIndexRequest";
import { BedIndexResponse } from "./types/locations/responses/BedIndexResponse";
import { BedIndexRequest } from "./types/locations/requests/BedIndexRequest";
import { RoomModel } from "./types/locations/models/RoomModel";
import { ResidentBedModel } from "./types/locations/models/ResidentBedModel";
import { CreateLocationRequest } from "./types/locations/requests/CreateLocationRequest";

export const locationsApi = createApi({
  reducerPath: "locationsApi",
  baseQuery: DynamicBaseQuery,
  keepUnusedDataFor: 10,
  tagTypes: ["Locations", "Floors", "Rooms", "Beds"],
  endpoints: (builder) => ({
    getAllLocations: builder.query<LocationsIndexResponse, void>({
      query: () => ({
        url: "/locations",
        method: "GET",
      }),
      providesTags: ["Locations"],
    }),
    getLocationsList: builder.query<LocationsIndexResponse, void>({
      query: () => ({
        url: "/locations/list",
        method: "GET",
      }),
      providesTags: ["Locations"],
    }),
    getSingleLocation: builder.query<LocationModel, { location: number }>({
      query: ({ location }) => ({
        url: `/locations/${location}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { location }) => [
        { type: "Locations", id: location },
      ],
    }),
    getAllFloors: builder.query<FloorsIndexResponse, { locationId?: number }>({
      query: ({ locationId }) => ({
        url: `/floors?filter[location_id]=${locationId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { locationId }) => {
        return [{ type: "Floors", id: locationId }];
      },
    }),
    createFloor: builder.mutation<FloorModel, FloorRequest>({
      query: (body) => ({
        url: "/floors",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { location_id: locationId }) => {
        return [{ type: "Locations", id: locationId }, { type: "Floors" }];
      },
    }),
    updateFloor: builder.mutation<
      FloorModel,
      { floorId: number; body: FloorRequest }
    >({
      query: ({ floorId, body }) => ({
        url: `/floors/${floorId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (
        _result,
        _error,
        { body: { location_id: locationId } },
      ) => {
        return [{ type: "Floors", id: locationId }];
      },
    }),
    deleteFloor: builder.mutation<
      FloorsIndexResponse,
      { floorId: number; locationId?: number }
    >({
      query: ({ floorId }) => ({
        url: `/floors/${floorId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { locationId }) => [
        { type: "Locations", id: locationId },
        { type: "Floors" },
      ],
    }),
    getAllRooms: builder.query<RoomsIndexResponse, RoomIndexRequest>({
      query: (params) => ({
        url: "/rooms",
        method: "GET",
        params,
      }),
      providesTags: (result, _error, { filter }) => {
        return [
          {
            type: "Rooms" as const,
            id: `location-${filter?.location_id}`,
          },
          {
            type: "Rooms" as const,
            id: `floor-${filter?.floor_id}`,
          },
          ...(result?.map((room) => ({
            type: "Rooms" as const,
            id: `room-${room.id}`,
          })) || []),
        ];
      },
    }),
    createRoom: builder.mutation<
      RoomModel,
      { body: RoomRequest; locationId: number }
    >({
      query: ({ body }) => ({
        url: "/rooms",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { locationId }) => {
        return [{ type: "Locations", id: locationId }, { type: "Rooms" }];
      },
    }),
    updateRoom: builder.mutation<
      RoomModel,
      { roomId: number; body: RoomRequest; locationId: number }
    >({
      query: ({ roomId, body }) => ({
        url: `/rooms/${roomId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { roomId }) => {
        return [{ type: "Rooms", id: `room-${roomId}` }];
      },
    }),
    deleteRoom: builder.mutation<
      SuccessResponse,
      { roomId: number; locationId: number }
    >({
      query: ({ roomId }) => ({
        url: `/rooms/${roomId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { locationId }) => [
        { type: "Locations", id: locationId },
        { type: "Rooms" },
      ],
    }),
    getAllBeds: builder.query<BedIndexResponse, BedIndexRequest>({
      query: (params) => ({
        url: "/beds",
        method: "GET",
        params,
      }),
      providesTags: (result, _error, { filter }) => {
        return [
          {
            type: "Beds" as const,
            id: `room-${filter?.room_id || "all"}`,
          },
          ...(result?.map((bed) => ({
            type: "Beds" as const,
            id: `bed-${bed.id}`,
          })) || []),
        ];
      },
    }),
    createBed: builder.mutation<ResidentBedModel, BedRequest>({
      query: (body) => ({
        url: "/beds",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { room_id }) => {
        return [{ type: "Beds", id: `room-${room_id}` }];
      },
    }),
    updateBed: builder.mutation<
      ResidentBedModel,
      { bedId: number; body: BedRequest }
    >({
      query: ({ bedId, body }) => ({
        url: `/beds/${bedId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { bedId }) => {
        return [{ type: "Beds", id: `bed-${bedId}` }];
      },
    }),
    deleteBed: builder.mutation<SuccessResponse, { bedId: number }>({
      query: ({ bedId }) => ({
        url: `/beds/${bedId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Beds"],
    }),
    createLocation: builder.mutation<LocationModel, CreateLocationRequest>({
      query: (body) => ({
        url: "/locations",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Locations"],
    }),
  }),
});
export const {
  useGetAllLocationsQuery,
  useLazyGetLocationsListQuery,
  useGetSingleLocationQuery,
  useGetAllFloorsQuery,
  useGetAllRoomsQuery,
  useGetAllBedsQuery,
  useCreateFloorMutation,
  useUpdateFloorMutation,
  useDeleteFloorMutation,
  useCreateRoomMutation,
  useUpdateRoomMutation,
  useDeleteRoomMutation,
  useCreateBedMutation,
  useUpdateBedMutation,
  useDeleteBedMutation,
  useCreateLocationMutation,
  usePrefetch,
} = locationsApi;
